import { z } from 'zod'

const optionalUrl = z.string().url('Please enter a valid URL').optional().or(z.literal(''))

const meetAndGreetType = z.enum(['meetAndGreet', 'custom'])

export const isMeetAndGreetType = (value: string): value is z.infer<typeof meetAndGreetType> =>
  meetAndGreetType.safeParse(value).success

/** Base schema — always validated. */
const baseSchema = z.object({
  // General
  eventName: z.string().min(1, 'Event name is required'),
  timezone: z.string().min(1, 'Timezone is required'),
  eventDate: z.string().min(1, 'Event date is required'),
  displayUntil: z.string().optional(),
  publishAt: z.string().optional(),
  isPinned: z.boolean(),
  imageUrl: z.string().optional(),

  // Location
  venueName: z.string().min(1, 'Venue name is required'),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipCode: z.string().optional(),
  country: z.string().min(1, 'Country is required'),

  // Ticket sales
  ticketButtonText: z.string().optional(),
  ticketUrl: optionalUrl,

  // Section enabled flags — drive conditional validation
  ticketSalesEnabled: z.boolean(),
  earlyAccessEnabled: z.boolean(),
  presaleEnabled: z.boolean(),
  vipEnabled: z.boolean(),
  addonsEnabled: z.boolean(),

  // Early venue access
  rsvpButtonText: z.string().optional(),
  rsvpCapacity: z
    .union([
      z.coerce.number<string | number>().int().min(0, 'Must be a non-negative whole number'),
      z.literal(''),
      z.nan()
    ])
    .optional(),
  rsvpStart: z.string().optional(),
  rsvpEnd: z.string().optional(),
  rsvpInfo: z.string().optional(),
  rsvpLeaveInfo: z.string().optional(),

  // Ticket pre-sales
  presaleStart: z.string().optional(),
  presaleEnd: z.string().optional(),
  presaleCode: z.string().optional(),
  hasPresalePools: z.boolean().optional(),

  // VIP ticket sales
  vipButtonText: z.string().optional(),
  vipUrl: optionalUrl,

  // Band add-ons (Meet & Greet)
  meetAndGreetType: meetAndGreetType.optional(),
  meetButtonText: z.string().optional(),
  meetAndGreetCapacity: z
    .union([
      z.coerce.number<string | number>().int().min(0, 'Must be a non-negative whole number'),
      z.literal(''),
      z.nan()
    ])
    .optional(),
  meetAndGreetStart: z.string().optional(),
  meetAndGreetEnd: z.string().optional(),
  meetAndGreetJoinText: z.string().optional(),
  meetAndGreetLeaveText: z.string().optional()
})

/** Full schema with conditional refinements based on enabled sections. */
export const eventFormSchema = baseSchema
  // Ticket sales: if enabled, both fields required
  .refine((d) => !d.ticketSalesEnabled || (d.ticketButtonText && d.ticketButtonText.length > 0), {
    message: "Button text is required. Disable 'Ticket Sales' if you don't want to use this field.",
    path: ['ticketButtonText']
  })
  .refine((d) => !d.ticketSalesEnabled || (d.ticketUrl && d.ticketUrl.length > 0), {
    message: "Ticket URL is required. Disable 'Ticket Sales' if you don't want to use this field.",
    path: ['ticketUrl']
  })
  // Early access: if enabled, rsvpButtonText is required
  .refine((d) => !d.earlyAccessEnabled || (d.rsvpButtonText && d.rsvpButtonText.length > 0), {
    message: 'Early Access button text is required when early venue access is enabled',
    path: ['rsvpButtonText']
  })
  // Presale: if enabled and no linked pool, presaleCode is required
  .refine((d) => !d.presaleEnabled || d.hasPresalePools || (d.presaleCode && d.presaleCode.length > 0), {
    message: 'You must enter a presale code or create presale unique codes',
    path: ['presaleCode']
  })
  // Presale code gates the ticket sales link, so it requires the URL + button text
  .refine(
    (d) => !(d.presaleEnabled && d.presaleCode && d.presaleCode.length > 0) || (d.ticketUrl && d.ticketUrl.length > 0),
    {
      message: 'Ticket URL is required when a presale code is set',
      path: ['ticketUrl']
    }
  )
  .refine(
    (d) =>
      !(d.presaleEnabled && d.presaleCode && d.presaleCode.length > 0) ||
      (d.ticketButtonText && d.ticketButtonText.length > 0),
    {
      message: 'Button text is required when a presale code is set',
      path: ['ticketButtonText']
    }
  )
  // VIP: if enabled, both fields required
  .refine((d) => !d.vipEnabled || (d.vipButtonText && d.vipButtonText.length > 0), {
    message: "Button text is required. Disable 'VIP ticket sales' if you don't want to use this field.",
    path: ['vipButtonText']
  })
  .refine((d) => !d.vipEnabled || (d.vipUrl && d.vipUrl.length > 0), {
    message: "VIP URL is required. Disable 'VIP ticket sales' if you don't want to use this field.",
    path: ['vipUrl']
  })
  // Addons: if enabled, button text + start/end required
  .refine((d) => !d.addonsEnabled || (d.meetButtonText && d.meetButtonText.length > 0), {
    message: 'Button text is required when add-ons are enabled',
    path: ['meetButtonText']
  })
  .refine((d) => !d.addonsEnabled || (d.meetAndGreetStart && d.meetAndGreetStart.length > 0), {
    message: 'Start date is required when add-ons are enabled',
    path: ['meetAndGreetStart']
  })
  .refine((d) => !d.addonsEnabled || (d.meetAndGreetEnd && d.meetAndGreetEnd.length > 0), {
    message: 'End date is required when add-ons are enabled',
    path: ['meetAndGreetEnd']
  })

export type EventFormData = z.input<typeof baseSchema>

/** Default values for a blank event form — matches the baseSchema shape. */
export const defaultEventFormData = (): EventFormData => ({
  eventName: '',
  timezone: '',
  eventDate: '',
  displayUntil: '',
  publishAt: '',
  isPinned: false,
  imageUrl: undefined,
  venueName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  ticketButtonText: '',
  ticketUrl: '',
  ticketSalesEnabled: false,
  earlyAccessEnabled: false,
  presaleEnabled: false,
  vipEnabled: false,
  addonsEnabled: false,
  rsvpButtonText: '',
  rsvpCapacity: undefined,
  rsvpStart: '',
  rsvpEnd: '',
  rsvpInfo: '',
  rsvpLeaveInfo: '',
  presaleStart: '',
  presaleEnd: '',
  presaleCode: '',
  hasPresalePools: false,
  vipButtonText: '',
  vipUrl: '',
  meetAndGreetType: 'meetAndGreet',
  meetButtonText: '',
  meetAndGreetCapacity: undefined,
  meetAndGreetStart: '',
  meetAndGreetEnd: '',
  meetAndGreetJoinText: '',
  meetAndGreetLeaveText: ''
})

/** Returns a flat map of field → error message, or null if valid. */
export const validateEventForm = (data: EventFormData): Record<string, string> | null => {
  const result = eventFormSchema.safeParse(data)
  if (result.success) return null

  const errors: Record<string, string> = {}
  for (const issue of result.error.issues) {
    const key = issue.path[0]
    if (typeof key === 'string' && !errors[key]) {
      errors[key] = issue.message
    }
  }
  return errors
}
